import { Text } from "../../../components/Text";
import { Chip } from "primereact/chip";
import { formatChatsDate } from "../../../utils/datemaker";
import PieChartTimer from "../PieChartTimer";
import { ReactComponent as AudioIcon } from "../../../svg/audio.svg";
import { useState, useEffect } from "react";
import { formatMobNumber, maskMobNumber } from "../../../utils/commonUtils";
import permissions from "../../../roles/permissions";
import modulesName from "../../../roles/modulesName";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "primereact/tooltip";
import { fetchAssignedTagsByCustomer } from "../../../reducers/tagslices";
import "../tags/tagStyle.css";

const ticketStatusColor = {
  New: "#005068",
  "In Progress": "#2DBECD",
  "Follow Up": "#000000",
  Closed: "#767676",
};

function CustomerChatInfo({ customer = null }) {
  const [currentTime, setCurrentTime] = useState(new Date().getTime());
  const { accesskey } = useSelector((state) => state.authenticate);
  const { user } = useSelector((state) => state.enqChats);
  const { tags = [] } = useSelector((state) => state.tag);
  const dispatch = useDispatch();
  const [assignedTagIds, setAssignedTagIds] = useState([]);
  const [displayTags, setDisplayTags] = useState([]);
  const withFullOpacity = (color) =>
    typeof color === "string" && color.length === 9
      ? `${color.slice(0, 7)}4D`
      : `${color}4D`;
  function handelChatLength1(msg) {
    if (msg) {
      let __msg = msg?.split(" ");
      if (__msg.length > 5) {
        return __msg?.slice(0, 5).join(" ") + "...";
      }
    }
    return msg;
  }

  function handelChatLength(msg) {
    let text = "";

    // Handle case where msg is an object with content property
    if (typeof msg === "string") {
      text = msg;
    } else if (
      msg &&
      typeof msg === "object" &&
      typeof msg.content === "string"
    ) {
      text = msg.content;
    }

    if (text) {
      const words = text.split(" ");
      if (words.length > 5) {
        return words.slice(0, 5).join(" ") + "...";
      }
      return text;
    }

    return "";
  }
  function customerLastMessage(customer) {
    const lastMessageTypeIcon = (messageType) => {
      switch (messageType) {
        case "image":
          return <i className="pi pi-camera" />;
        case "video":
          return <i className="pi pi-video" />;
        case "audio":
          return (
            <>
              <AudioIcon />
              <span>audio</span>
            </>
          );
        case "file":
          return <i className="pi pi-file" />;
        default:
          return;
      }
    };
    return (
      <span className="ch-limit-30">
        {lastMessageTypeIcon(customer.lastMessage.messageType)}{" "}
        {customer?.lastMessage?.text
          ? handelChatLength(customer.lastMessage.text)
          : customer.lastMessage.messageType === "audio"
          ? null
          : customer.lastMessage.messageType}
      </span>
    );
  }

  useEffect(() => {
    // Update the current time every second
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 1000);

    // Cleanup the interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (Array.isArray(customer?.tag)) {
      setAssignedTagIds(customer.tag);
      return;
    }
    if (customer?.phone === null || customer?.phone === undefined) return;
    const normalizedPhone = String(customer.phone).replace(/\D+/g, "");
    if (!normalizedPhone) {
      setAssignedTagIds([]);
      return;
    }
    dispatch(fetchAssignedTagsByCustomer(normalizedPhone))
      .unwrap()
      .then((data) => {
        setAssignedTagIds(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAssignedTagIds([]);
      });
  }, [customer?.phone, customer?.tag, dispatch]);

  useEffect(() => {
    if (!assignedTagIds?.length || !tags?.length) {
      setDisplayTags([]);
      return;
    }
    const mappedTags = assignedTagIds
      .map((id) => tags.find((tag) => tag.id === id))
      .filter(Boolean)
      .map((tag) => ({
        id: tag.id,
        label: `#${tag.tagName}`,
        color: tag.tagColor,
      }));
    setDisplayTags(mappedTags);
  }, [assignedTagIds, tags]);
  const getChatPhone = (custInfo) =>
    accesskey[modulesName.CUSTOMER]?.includes(permissions.READ)
      ? formatMobNumber(custInfo.phone)
      : maskMobNumber(custInfo.phone);
  const capitalizeName = (name) =>
    name.charAt(0).toUpperCase() + name.substring(1);
  const getChatName = (custInfo) =>
    custInfo?.businessName
      ? capitalizeName(custInfo.businessName)
      : getChatPhone(custInfo);

  const isBlocked = customer?.isOptOut;

  return (
    <>
      {customer !== null && (
        <div className={`userChatInfo flex flex-column gap-1`}>
          <Tooltip target=".block-target-icon" />
          <div className="flex justify-content-between">
            <div className="flex">
              <Text type={"sub-heading"} cust="ch-limit-30">
                <span>{getChatName(customer)}</span>
              </Text>
              {user?.phone !== customer?.phone &&
              customer?.unreadMessages !== 0 &&
              customer?.unreadMessages > 0 ? (
                <span className="dot-unread">{customer?.unreadMessages}</span>
              ) : (
                ""
              )}
            </div>

            <div className="flex align-items-center">
              <Text type={"small-text"}>
                {formatChatsDate(customer.lastUpdatedAt)}
              </Text>
              <div className="ml-2 flex justify-content-center align-items-center gap-2">
                {isBlocked && (
                  <i
                    className="block-target-icon pi pi-ban"
                    data-pr-tooltip="This contact is blocked."
                    data-pr-position="right"
                    data-pr-at="right+5 top"
                    data-pr-my="left center-2"
                    style={{ color: "red", fontSize: "1.26rem" }}
                  />
                )}
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    border: "1px solid rgba(0, 0, 0, 0.7)",
                    borderRadius: "50%",
                    padding: "2px",
                  }}
                >
                  <PieChartTimer
                    currentTime={currentTime}
                    startTimestamps={customer.enqStartedAt}
                    duration={24 * 60 * 60 * 1000}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-content-between gap-2 align-items-center">
            <Text
              type={"small-text"}
              style={{
                opacity: "0.6",
              }}
              cust="ch-limit-30"
            >
              {customerLastMessage(customer)}
            </Text>

            <Text
              type={"small-text"}
              cust={`font-weigh-700`}
              style={{ color: ticketStatusColor[customer?.ticketStatus] }}
            >
              {customer?.ticketStatus}
            </Text>
          </div>
          {displayTags?.length ? (
            <div className="flex flex-wrap gap-1">
              {displayTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.label}
                  style={{
                    backgroundColor: withFullOpacity(tag.color),
                    color: "#111",
                    fontSize: "0.65rem",
                  }}
                  className="tag-chip px-2 py-0.5 leading-none relative"
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}

export default CustomerChatInfo;
